/* eslint-disable react-hooks/exhaustive-deps */
import { ROLE, STATUS, type TStatus } from '@datacloud-incubation/frontend-ai-chat-module';
import { notificationsService } from '@datacloud-incubation/frontend-notifications-module';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { concatMap, Subject } from 'rxjs';
import { INIT_PAGINATION, PAGINATION_ORDER } from '@core/constants/pagination.constant';
import { useTanstackQuery } from '@core/hooks/useTanstackQuery';
import { FLOW_CHAT_ANSWER_STATUS, FLOW_CHAT_EVENTS } from '../constants/flowChat.constant';
import { abcloudzFlowChatQuery } from '../queries/abcloudzFlowChat.query';
import { abcloudzStreamService } from '../services/AbcloudzStream.service';
import { initialState, reducer } from '../utils/useFlowChatHistory.util';
import useAbcloudzFlowChatSessions from './useAbcloudzFlowChatSessions';
import type { TFlowChatAnswerStatus, TSubmitMsg } from '../models/flowChat.model';
import type { TUseFlowChatHistoryParams } from '../models/useFlowChatHistory.model';
import type { TPagination } from '@core/models/pagination.model';

const useAbcloudzFlowChatHistory = ({ flowId, selectedSession }: TUseFlowChatHistoryParams) => {
  const sessionId = selectedSession?.id ?? '';
  const [chatAnswerStatus, setChatAnswerStatus] = useState<TFlowChatAnswerStatus>(
    FLOW_CHAT_ANSWER_STATUS.completed
  );
  const [isMessageStart, setIsMessageStart] = useState(false);

  const [status, setStatus] = useState<TStatus>(STATUS.idle);

  const [message, setMessage] = useState('');

  const [controller, setController] = useState(new AbortController());

  const [reducerState, dispatch] = useReducer(reducer, initialState);
  const onSubmit$ = useRef(new Subject<TSubmitMsg>());

  const pagination: TPagination = INIT_PAGINATION;
  const searchParams = {
    flowId,
    sessionId,
    limit: pagination.limit,
    offset: pagination.offset,
    order: PAGINATION_ORDER.DESC
  };

  const getChatHistory = useTanstackQuery({
    instance: abcloudzFlowChatQuery.getChatHistory$(searchParams),
    dependencies: [sessionId]
  });

  const { abortSession } = useAbcloudzFlowChatSessions();

  useEffect(() => {
    if (sessionId && getChatHistory.data) {
      dispatch({
        event: FLOW_CHAT_EVENTS.idle,
        data: getChatHistory.data.messages.reverse()
      });

      setChatAnswerStatus(getChatHistory.data.answerStatus ?? FLOW_CHAT_ANSWER_STATUS.completed);
    }

    return () => {
      dispatch({
        event: FLOW_CHAT_EVENTS.idle,
        data: []
      });
    };
  }, [sessionId, getChatHistory]);

  const onSendMessage = (message: string) => {
    if (sessionId) {
      onSubmit$.current.next({
        content: message.trim(),
        sessionId
      });

      dispatch({
        event: FLOW_CHAT_EVENTS.userMessage,
        data: {
          id: crypto.randomUUID(),
          session: sessionId,
          sessionId,
          content: message.trim(),
          created: new Date(Date.now()).toString(),
          createdAt: new Date(Date.now()).toString(),
          role: ROLE.user
        }
      });
    }
  };

  const clearMessage = () => {
    setMessage('');
  };

  const onSubmit = useCallback(() => {
    if (sessionId) {
      onSendMessage(message);

      clearMessage();
    }
  }, [message, sessionId]);

  const onStop = useCallback(() => {
    void (async () => {
      try {
        await abortSession.mutate(
          { flowId, sessionId },
          {
            onSuccess: () => {
              setStatus(STATUS.succeeded);
              controller.abort();
              setController(new AbortController());
              setChatAnswerStatus(FLOW_CHAT_ANSWER_STATUS.completed);
            },
            onError: () => {
              notificationsService.errorNotification('Failed to abort session');
            }
          }
        );
      } catch (_error) {
        //
      }
    })();
  }, [controller, flowId, sessionId, abortSession]);

  useEffect(() => {
    setStatus(STATUS.succeeded);
    const subscription = onSubmit$.current
      .asObservable()
      .pipe(
        concatMap(({ content, sessionId }) => {
          return abcloudzStreamService.chatMsgStream$({
            flowId,
            dto: { content, sessionId },
            options: {
              signal: controller.signal
            }
          });
        })
      )
      .subscribe({
        next: (value) => {
          dispatch(value);
          if (value.event === FLOW_CHAT_EVENTS.messageStart) {
            setIsMessageStart(true);
          }
          if (value.event === FLOW_CHAT_EVENTS.messageDelta) {
            setIsMessageStart(false);
          }
          if (value.event === FLOW_CHAT_EVENTS.complete) {
            setStatus(STATUS.succeeded);
          } else {
            setStatus(STATUS.pending);
          }
        },
        error: () => {
          controller.abort();
          setController(new AbortController());
          setStatus(STATUS.failed);
        }
      });

    return () => {
      controller.abort();
      subscription.unsubscribe();
    };
  }, [controller, flowId, sessionId]);

  useEffect(() => {
    if (chatAnswerStatus === FLOW_CHAT_ANSWER_STATUS.completed || !sessionId) {
      return undefined;
    }

    const subscription = abcloudzStreamService
      .restoreChatStream$({
        flowId,
        sessionId,
        options: {
          signal: controller.signal
        }
      })
      .subscribe({
        next: (value) => {
          dispatch(value);
          if (value.event === FLOW_CHAT_EVENTS.complete) {
            setStatus(STATUS.succeeded);
          } else {
            setStatus(STATUS.pending);
          }
        },
        error: () => {
          controller.abort();
          setController(new AbortController());
          setStatus(STATUS.failed);
        }
      });

    return () => {
      controller.abort();
      subscription.unsubscribe();
    };
  }, [controller, flowId, sessionId, chatAnswerStatus]);

  const { messages, sessionMetadata } = reducerState;

  return {
    getChatHistory,
    status,
    setStatus,
    dispatch,
    messages,
    message,
    setMessage,
    onSubmit,
    onStop,
    sessionMetadata,
    isMessageStart,
    clearMessage
  };
};

export default useAbcloudzFlowChatHistory;
